import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from "@angular/material/dialog";
import { AccountService } from "../../../services/account.service";
import {
  GeneralConfirmationModalComponent
} from "../../../components/general-confirmation-modal/general-confirmation-modal.component";
import { GeneralConfirmationModalData, GeneralConfirmationModalResult } from "../../../models";
import { TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import { DialogService } from "../../../services/dialog.service";

@Component({
  selector: 'cpf-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ChangePasswordComponent {
  @Input() showBackButton = true;

  @Output() forgotPasswordClicked = new EventEmitter<void>();
  @Output() cancelClicked = new EventEmitter<void>();
  @Output() successClicked = new EventEmitter<void>();

  oldPassword = '';
  newPassword = '';
  confirmPassword = '';
  errors: string[] = [];

  showOldPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  constructor(private accountService: AccountService,
              private dialog: MatDialog,
              private translate: TranslateService,
              private router: Router,
              private dialogService: DialogService,) {

  }

  validate(): boolean {
    this.errors = []; // Clear previous errors
    const minLength = 8;

    if (this.oldPassword.length < minLength) {
      this.errors.push('change-password.validation.old-password-min-length');
    }

    const password = this.newPassword;

    if (password.length < minLength) {
      this.errors.push('change-password.validation.new-password-min-length');
    }
    if (!/\d/.test(password)) {
      this.errors.push('change-password.validation.new-password-require-number');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      this.errors.push('change-password.validation.new-password-require-special');
    }
    if (!/[A-Z]/.test(password)) {
      this.errors.push('change-password.validation.new-password-require-uppercase');
    }
    if (!/[a-z]/.test(password)) {
      this.errors.push('change-password.validation.new-password-require-lowercase');
    }

    if (this.newPassword !== this.confirmPassword) {
      this.errors.push('change-password.validation.password-mismatch');
    }

    if (this.errors.length > 0) {
      return false;
    }

    return true;
  }

  async saveChanges() {
    if (this.validate()) {
      const response = await this.accountService.handleUpdatePassword({
        oldPassword: this.oldPassword,
        newPassword: this.newPassword
      });

      if (response === "error") {
        return this.showFailModal();
      } else {
        this.oldPassword = '';
        this.newPassword = '';
        this.confirmPassword = '';
        return this.showSuccessModal();
      }
    }
  }

  showFailModal() {
    this.translate.get(['change-password.modal.fail.title', 'change-password.modal.fail.message']).subscribe(translations => {
      const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, {
        data: {
          title: translations['change-password.modal.fail.title'],
          message: translations['change-password.modal.fail.message'],
          confirmButton: GeneralConfirmationModalResult.Confirm
        } as GeneralConfirmationModalData
      });

      dialogRef.afterClosed().subscribe(result => {

      });
    });
  }

  showSuccessModal() {
    this.translate.get(['change-password.modal.success.title', 'change-password.modal.success.message']).subscribe(translations => {
      const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, {
        data: {
          title: translations['change-password.modal.success.title'],
          message: translations['change-password.modal.success.message'],
          confirmButton: GeneralConfirmationModalResult.Confirm
        } as GeneralConfirmationModalData
      });

      dialogRef.afterClosed().subscribe(result => {
        if (this.successClicked.observed) {
          this.successClicked.emit();
        } else {
          this.router.navigate(['calingo', 'account', 'view']);
        }
      });
    });
  }

  forgotPassword() {
    this.translate.get(['change-password.modal.forgot-password.title', 'change-password.modal.forgot-password.message']).subscribe(translations => {
      const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, {
        data: {
          title: translations['change-password.modal.forgot-password.title'],
          message: translations['change-password.modal.forgot-password.message'],
          confirmButton: GeneralConfirmationModalResult.Confirm,
          cancelButton: GeneralConfirmationModalResult.Cancel,
        } as GeneralConfirmationModalData
      });

      dialogRef.afterClosed().subscribe(async result => {
        if (result === GeneralConfirmationModalResult.Confirm) {
          const res = await this.accountService.sendCode();

          if (res === "error") {
            this.showFailModal();
            return;
          }

          if (this.forgotPasswordClicked.observed) {
            this.forgotPasswordClicked.emit();
            return;
          }
          this.router.navigate(['calingo', 'account', 'forgot-password']);
        }
      });
    })
  }

  navigateBack() {
    if (this.cancelClicked.observed) {
      this.cancelClicked.emit();
      return;
    }
    this.router.navigate(['calingo', 'account', 'view']);
  }
}
